import React, { useCallback, useEffect, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input, Label, Textarea } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { toast } from "sonner";
import { Layers, Plus, GitBranch, Pencil, ArrowUpRight, Import, Lightbulb, Check } from "lucide-react";
import { PageShell } from "./PageShell";

const STATUS_TONE = { draft: "outline", review: "warning", published: "success", deprecated: "danger" };
const NEXT_STATUS = { draft: ["review"], review: ["published", "draft"], published: ["deprecated"], deprecated: ["draft"] };
const EDIT_SECTIONS = ["description", "keywords", "value_chain", "material_topics", "attributes", "kpis", "data_sources", "supplier_categories", "risks", "framework_mappings", "financing_opportunities"];

export default function PlatformTemplates() {
  const [templates, setTemplates] = useState([]);
  const [customAttrs, setCustomAttrs] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ code: "", industry_key: "", industry_name: "", vertical_name: "", description: "" });
  const [versionTarget, setVersionTarget] = useState(null);
  const [versionForm, setVersionForm] = useState({ version: "", release_notes: "" });
  const [editTarget, setEditTarget] = useState(null);
  const [editJson, setEditJson] = useState("");
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [promoteAttr, setPromoteAttr] = useState("");
  const [insights, setInsights] = useState(null);
  const [insightCluster, setInsightCluster] = useState(null);
  const [insightTargetId, setInsightTargetId] = useState("");

  const load = useCallback(() => {
    api.get("/valueprint/admin/templates").then((r) => setTemplates(r.data.templates)).catch(() => {});
    api.get("/valueprint/admin/custom-attributes").then((r) => setCustomAttrs(r.data.attributes)).catch(() => {});
    api.get("/valueprint/admin/attribute-insights").then((r) => setInsights(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (t, status) => {
    try {
      await api.post(`/valueprint/admin/templates/${t.id}/status`, { status });
      toast.success(`${t.vertical_name} v${t.version} → ${status}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const create = async () => {
    try {
      await api.post("/valueprint/admin/templates", createForm);
      toast.success("Draft template created");
      setCreateOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const newVersion = async () => {
    try {
      await api.post(`/valueprint/admin/templates/${versionTarget.id}/new-version`, versionForm);
      toast.success(`Draft v${versionForm.version} created`);
      setVersionTarget(null); setVersionForm({ version: "", release_notes: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openEdit = (t) => {
    const content = Object.fromEntries(EDIT_SECTIONS.map((k) => [k, t[k]]));
    setEditJson(JSON.stringify(content, null, 2));
    setEditTarget(t);
  };
  const saveEdit = async () => {
    let parsed;
    try { parsed = JSON.parse(editJson); } catch { toast.error("Invalid JSON"); return; }
    try {
      await api.patch(`/valueprint/admin/templates/${editTarget.id}`, parsed);
      toast.success("Template content updated");
      setEditTarget(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const promote = async () => {
    const [tenantId, key] = promoteAttr.split("::");
    try {
      await api.post(`/valueprint/admin/templates/${promoteTarget.id}/promote-attribute`, { tenant_id: tenantId, attribute_key: key });
      toast.success("Attribute promoted into the template");
      setPromoteTarget(null); setPromoteAttr("");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const promoteFromInsight = async () => {
    const src = insightCluster.tenants.find((t) => t.enabled) || insightCluster.tenants[0];
    try {
      await api.post(`/valueprint/admin/templates/${insightTargetId}/promote-attribute`, { tenant_id: src.tenant_id, attribute_key: src.key });
      toast.success(`“${insightCluster.name}” promoted into the official template`);
      setInsightCluster(null); setInsightTargetId("");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const grouped = templates.reduce((acc, t) => { (acc[t.industry_name] = acc[t.industry_name] || []).push(t); return acc; }, {});

  return (
    <PageShell title="ValuePrint Template Studio" testid="platform-templates-page"
      subtitle="Govern the platform template library — draft, review, publish and deprecate industry/vertical templates, cut new versions with release notes, and promote tenant custom attributes into the official templates."
      actions={<Button variant="primary" size="sm" onClick={() => setCreateOpen(true)} data-testid="ts-new-template-btn"><Plus className="h-3.5 w-3.5" /> New template</Button>}>

      {!!insights?.clusters?.length && (
        <div className="card-flat overflow-hidden" data-testid="ts-insights-card">
          <div className="px-4 py-2.5 border-b border-border bg-surface flex items-center gap-2 flex-wrap">
            <Lightbulb className="h-4 w-4 text-[#FFB800]" />
            <span className="font-display font-semibold text-sm">Adoption insights — what tenants add on top of the templates</span>
            <Badge variant="outline" className="text-[10px]">{insights.total_custom_attributes} custom attributes across tenants</Badge>
          </div>
          <div className="divide-y divide-border">
            {insights.clusters.map((c) => (
              <div key={c.norm} className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`ts-insight-${c.norm}`}>
                <div className="min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{c.name}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">{c.data_type}{c.unit ? ` · ${c.unit}` : ""}</span>
                    <Badge variant={c.count >= 2 ? "lime" : "outline"} className="text-[9px]" data-testid={`ts-insight-count-${c.norm}`}>
                      {c.count >= 2 ? `${c.count} tenants — promotion candidate` : "1 tenant"}
                    </Badge>
                    {c.promoted_in.map((p) => (
                      <Badge key={p} variant="success" className="text-[9px]"><Check className="h-2.5 w-2.5 mr-0.5" /> in {p}</Badge>
                    ))}
                  </div>
                  {c.why && <p className="text-[11px] text-muted-foreground max-w-2xl">{c.why}</p>}
                  <div className="flex flex-wrap gap-1.5">
                    {c.tenants.map((t) => (
                      <span key={`${t.tenant_id}-${t.key}`} className="text-[10px] font-mono px-1.5 py-0.5 border border-border rounded-sm" title={`Added by ${t.added_by || "—"}`}>
                        {t.tenant_name}{t.primary_template ? ` · ${t.primary_template}` : ""}{t.enabled === false ? " (disabled)" : ""}
                      </span>
                    ))}
                  </div>
                </div>
                {!!c.suggested_targets.length && (
                  <Button size="sm" variant="outline" onClick={() => { setInsightCluster(c); setInsightTargetId(c.suggested_targets[0]?.id || ""); }}
                    data-testid={`ts-insight-promote-${c.norm}`}>
                    <Import className="h-3 w-3" /> Promote into template…
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.entries(grouped).map(([industry, list]) => (
        <div key={industry} className="card-flat overflow-x-auto">
          <div className="px-4 py-2.5 border-b border-border bg-surface flex items-center gap-2">
            <Layers className="h-4 w-4 text-accent" /><span className="font-display font-semibold text-sm">{industry}</span>
            <Badge variant="outline" className="text-[10px]">{list.length}</Badge>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Vertical</TableHead><TableHead>Version</TableHead><TableHead>Status</TableHead><TableHead>Content</TableHead><TableHead>Tenants</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {list.map((t) => (
                <TableRow key={t.id} data-testid={`ts-row-${t.code}-${t.version}`}>
                  <TableCell>
                    <div className="text-sm font-medium">{t.vertical_name} {t.is_stub && <Badge variant="outline" className="text-[9px] ml-1">stub</Badge>}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{t.code}</div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">v{t.version}</TableCell>
                  <TableCell><Badge variant={STATUS_TONE[t.status]} className="text-[10px]" data-testid={`ts-status-${t.code}-${t.version}`}>{t.status}</Badge></TableCell>
                  <TableCell className="text-[10px] font-mono text-muted-foreground">
                    {(t.attributes || []).length} attr · {(t.risks || []).length} risks · {(t.kpis || []).length} KPIs · {(t.value_chain || []).length} stages
                  </TableCell>
                  <TableCell className="font-mono text-xs">{t.tenants_applied || 0}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(NEXT_STATUS[t.status] || []).map((s) => (
                        <Button key={s} size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => setStatus(t, s)} data-testid={`ts-move-${t.code}-${t.version}-${s}`}>
                          <ArrowUpRight className="h-2.5 w-2.5" /> {s}
                        </Button>
                      ))}
                      <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => { setVersionTarget(t); setVersionForm({ version: "", release_notes: "" }); }} data-testid={`ts-newver-${t.code}-${t.version}`}>
                        <GitBranch className="h-2.5 w-2.5" /> New version
                      </Button>
                      {t.status !== "deprecated" && (
                        <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => openEdit(t)} data-testid={`ts-edit-${t.code}-${t.version}`}>
                          <Pencil className="h-2.5 w-2.5" /> Edit
                        </Button>
                      )}
                      {t.status !== "deprecated" && !!customAttrs.length && (
                        <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => { setPromoteTarget(t); setPromoteAttr(""); }} data-testid={`ts-promote-${t.code}-${t.version}`}>
                          <Import className="h-2.5 w-2.5" /> Promote attr
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}

      <Dialog open={!!insightCluster} onOpenChange={(o) => !o && setInsightCluster(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Promote “{insightCluster?.name}” into a template</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">
            {insightCluster?.count >= 2
              ? `${insightCluster.count} tenants added this attribute independently — promoting it makes it standard for every tenant on the chosen template's next apply/upgrade.`
              : "Promoting makes this attribute standard on the chosen template."}
          </p>
          <div>
            <Label>Target template</Label>
            <Select value={insightTargetId} onValueChange={setInsightTargetId}>
              <SelectTrigger data-testid="ts-insight-target-select"><SelectValue placeholder="Choose a template…" /></SelectTrigger>
              <SelectContent>
                {(insightCluster?.suggested_targets || []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.vertical_name} v{t.version} ({t.status}) — used by these tenants</SelectItem>
                ))}
                {templates.filter((t) => t.status !== "deprecated" && !(insightCluster?.suggested_targets || []).some((s) => s.id === t.id)).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.vertical_name} v{t.version} ({t.status})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="primary" onClick={promoteFromInsight} disabled={!insightTargetId} data-testid="ts-insight-promote-submit">Promote into template</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New template (draft v0.1)</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code *</Label><Input value={createForm.code} onChange={(e) => setCreateForm({ ...createForm, code: e.target.value })} placeholder="industry-vertical" data-testid="ts-create-code" /></div>
            <div><Label>Industry key *</Label><Input value={createForm.industry_key} onChange={(e) => setCreateForm({ ...createForm, industry_key: e.target.value })} placeholder="e.g. retail" data-testid="ts-create-industry-key" /></div>
            <div><Label>Industry name *</Label><Input value={createForm.industry_name} onChange={(e) => setCreateForm({ ...createForm, industry_name: e.target.value })} data-testid="ts-create-industry-name" /></div>
            <div><Label>Vertical name *</Label><Input value={createForm.vertical_name} onChange={(e) => setCreateForm({ ...createForm, vertical_name: e.target.value })} data-testid="ts-create-vertical" /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} /></div>
          </div>
          <Button variant="primary" onClick={create} disabled={!createForm.code || !createForm.industry_key || !createForm.vertical_name} data-testid="ts-create-submit">Create draft</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!versionTarget} onOpenChange={(o) => !o && setVersionTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>New version of {versionTarget?.vertical_name} (current v{versionTarget?.version})</DialogTitle></DialogHeader>
          <div><Label>New version *</Label><Input value={versionForm.version} onChange={(e) => setVersionForm({ ...versionForm, version: e.target.value })} placeholder={versionTarget ? `e.g. ${(parseFloat(versionTarget.version) + 0.1).toFixed(1)}` : ""} data-testid="ts-version-input" /></div>
          <div><Label>Release notes *</Label><Textarea rows={3} value={versionForm.release_notes} onChange={(e) => setVersionForm({ ...versionForm, release_notes: e.target.value })} placeholder="What's new / retired in this version — shown to tenants in the upgrade prompt" data-testid="ts-release-notes" /></div>
          <Button variant="primary" onClick={newVersion} disabled={!versionForm.version || versionForm.release_notes.trim().length < 5} data-testid="ts-version-submit">Create draft version</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Edit content — {editTarget?.vertical_name} v{editTarget?.version}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Metadata-driven: edit the template sections as JSON. Changes apply to this version only.</p>
          <Textarea rows={18} value={editJson} onChange={(e) => setEditJson(e.target.value)} className="font-mono text-[11px]" data-testid="ts-edit-json" />
          <Button variant="primary" onClick={saveEdit} data-testid="ts-edit-save">Save content</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!promoteTarget} onOpenChange={(o) => !o && setPromoteTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Promote a tenant custom attribute into {promoteTarget?.vertical_name} v{promoteTarget?.version}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">When several tenants add the same custom attribute, promote it so every tenant on this template gets it as standard.</p>
          <div>
            <Label>Custom attribute</Label>
            <Select value={promoteAttr} onValueChange={setPromoteAttr}>
              <SelectTrigger data-testid="ts-promote-select"><SelectValue placeholder="Choose an attribute…" /></SelectTrigger>
              <SelectContent>
                {customAttrs.map((a) => (
                  <SelectItem key={`${a.tenant_id}::${a.key}`} value={`${a.tenant_id}::${a.key}`}>
                    {a.name} ({a.unit || a.data_type}) — {a.tenant_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="primary" onClick={promote} disabled={!promoteAttr} data-testid="ts-promote-submit">Promote into template</Button>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
